/**
 * Tipos soportados por `ui-file-upload`.
 *
 * Alias de alto nivel (p.ej. `word`, `excel`) para que el consumidor no
 * maneje la matriz de extensiones / MIME. El componente los traduce al
 * `accept` nativo y a la validación.
 */
export type UiFileKind =
  | 'pdf'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'csv'
  | 'xml';

export interface UiFileKindSpec {
  extensions: string[];
  mimeTypes: string[];
}

export const UI_FILE_KIND_MAP: Record<UiFileKind, UiFileKindSpec> = {
  pdf: { extensions: ['pdf'], mimeTypes: ['application/pdf'] },
  word: {
    extensions: ['doc', 'docx'],
    mimeTypes: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  },
  excel: {
    extensions: ['xls', 'xlsx'],
    mimeTypes: [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
  },
  powerpoint: {
    extensions: ['ppt', 'pptx'],
    mimeTypes: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
  },
  csv: {
    extensions: ['csv'],
    mimeTypes: ['text/csv', 'application/csv', 'application/vnd.ms-excel'],
  },
  xml: {
    extensions: ['xml'],
    mimeTypes: ['application/xml', 'text/xml'],
  },
};

/** Archivo ya guardado. Se muestra en la lista y no forma parte de la selección nueva. */
export interface UiStoredFileItem {
  id: string;
  name: string;
  size?: number;
  url?: string;
}

/** Representación de cada archivo seleccionado. */
export interface UiFileItem {
  /** Id local para tracking (no es un ID remoto). */
  id: string;
  file: File;
  name: string;
  /** Tamaño en bytes. */
  size: number;
  type: string;
  /** Extensión en minúsculas, sin punto. */
  extension: string;
  /**
   * `URL.createObjectURL(file)`. Se revoca al eliminar o destruir.
   */
  url: string;
}

export type UiFileRejectionReason =
  | 'too-large'
  | 'type-not-allowed'
  | 'max-files';

export interface UiFileRejection {
  name: string;
  size: number;
  reason: UiFileRejectionReason;
}
